import { useStore } from "utils/useStore";
import AgentStore from "./Stores/AgentStore";
import AgentDrawerContents from "./AgentDrawerContents";
import { Drawer, Grid, keyframes } from "@mui/material";
import React from "react";
import { observer } from "mobx-react";
import theme from "Theme/AppTheme";
import { FullscreenChevron } from "./Fullscreen/FullscreenChevron";
import { AgentTeaserDrawerContents } from "./AgentTeaserDrawerContents";
import { KboPaywallDialog } from "./KboPaywallDialog";

const fade = keyframes`
    from {
        opacity: 0
    }
    to {
        opacity: 1
    }
`;

export const AgentDrawer = observer(
    ({
        componentsProps,
        ModalProps,
        PaperProps,
        ...props
    }: React.ComponentPropsWithoutRef<typeof Drawer>) => {
        const agentStore = useStore(AgentStore);
        const [shouldRenderContents, setShouldRenderContents] =
            React.useState(false);

        const hasAccessToKbo = agentStore.canOpenKboThroughUI;

        React.useEffect(() => {
            if (!agentStore.isDrawerOpen || shouldRenderContents) return;

            let secondFrame = 0;
            const firstFrame = window.requestAnimationFrame(() => {
                secondFrame = window.requestAnimationFrame(() => {
                    setShouldRenderContents(true);
                });
            });

            return () => {
                window.cancelAnimationFrame(firstFrame);
                if (secondFrame) {
                    window.cancelAnimationFrame(secondFrame);
                }
            };
        }, [agentStore.isDrawerOpen, shouldRenderContents]);

        return (
            <>
                <KboPaywallDialog />
                {hasAccessToKbo && <FullscreenChevron />}

                <Drawer
                    id="kbo-drawer"
                    anchor="right"
                    transitionDuration={0}
                    hideBackdrop
                    disableEnforceFocus
                    disableScrollLock
                    sx={{ position: "initial" }}
                    open={agentStore.isDrawerOpen}
                    onClose={() => agentStore.closeDrawer()}
                    ModalProps={{
                        keepMounted: shouldRenderContents,
                        ...ModalProps,
                    }}
                    {...props}
                    componentsProps={{
                        ...componentsProps,
                        root: {
                            ...componentsProps?.root,
                            style: {
                                zIndex: theme.zIndex.appBar - 1,
                                maxWidth: "fit-content",
                                ...componentsProps?.root?.["style"],
                            },
                        },
                    }}
                    PaperProps={{
                        ...PaperProps,
                        sx: {
                            zIndex: theme.zIndex.appBar - 1,
                            marginTop: "72px",
                            maxHeight: "calc(100dvh - 72px)",
                            animation: `${fade} 300ms forwards`,
                            ...PaperProps?.["sx"],
                        },
                    }}
                >
                    {/* The fullscreen view owns the chat UI while expanded.
                        Keep the store alive, but unmount the hidden drawer UI
                        so inputs and message elements are not duplicated. */}
                    {agentStore.fullscreen ? null : !shouldRenderContents ? (
                        <Grid
                            height="100%"
                            width="calc(max(20dvw, 372px))"
                        />
                    ) : hasAccessToKbo ? (
                        <AgentDrawerContents
                            close={() => agentStore.closeDrawer()}
                        />
                    ) : (
                        <AgentTeaserDrawerContents />
                    )}
                </Drawer>
            </>
        );
    },
);
